import { isRecord } from '~/utils/isRecord';

import { EXPLICIT_FIELD_LABELS, LONG_TEXT_LENGTH } from './constants';
import { isUnsetValue } from './utils';

export function humanizeKey(key: string): string {
  if (EXPLICIT_FIELD_LABELS[key]) {
    return EXPLICIT_FIELD_LABELS[key];
  }

  return key
    .replace(/_id_cloudinary_external$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatFacilityCode(code: unknown): string {
  if (typeof code !== 'string') {
    return 'Facility';
  }

  return code
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function isFacilityItem(value: unknown): value is Record<string, unknown> & { facilityCode: string } {
  return isRecord(value) && typeof value.facilityCode === 'string';
}

export function formatFacilityItem(value: Record<string, unknown>): string {
  const label = formatFacilityCode(value.facilityCode);
  const rawValue = value.value;

  if ((typeof rawValue === 'string' || typeof rawValue === 'number') && String(rawValue).trim() !== '') {
    const numericValue = String(rawValue).trim();
    const facilityCode = typeof value.facilityCode === 'string' ? value.facilityCode : '';

    if (facilityCode.startsWith('NUMBER_OF_')) {
      const noun = label.replace(/^Number Of /i, '');
      return `${numericValue} ${noun}`;
    }

    return `${numericValue} ${label.toLowerCase()}`;
  }

  return label;
}

function formatBedType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getBedGroupItems(value: Record<string, unknown>): Array<string> {
  if (typeof value.description === 'string' && value.description.trim()) {
    return [value.description.trim()];
  }
  if (!Array.isArray(value.configuration)) {
    return [];
  }

  return value.configuration
    .map((item) => {
      if (!isRecord(item)) {
        return undefined;
      }

      const type = typeof item.type === 'string' ? formatBedType(item.type) : '';
      const quantity = typeof item.quantity === 'number' ? item.quantity : undefined;

      if (!type || quantity === undefined) {
        return undefined;
      }

      return `${quantity} ${type}`;
    })
    .filter(Boolean);
}

export function formatBedGroup(value: Record<string, unknown>): string | undefined {
  const configuration = getBedGroupItems(value);

  return configuration.length > 0 ? configuration.join(', ') : undefined;
}

export function formatSimpleValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'Not set';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }

  if (isRecord(value)) {
    if (typeof value.amount === 'number') {
      return `${value.amount}${typeof value.currency === 'string' ? ` ${value.currency}` : ''}`;
    }
    if (typeof value.value === 'number') {
      return `${value.value}%`;
    }
    if (typeof value.title === 'string') {
      return value.title;
    }
    if (typeof value.content === 'string') {
      return value.content;
    }
    if (typeof value.name === 'string') {
      return value.name;
    }
  }

  return 'Changed';
}

export function isLongTextValue(value: unknown): boolean {
  return typeof value === 'string' && (value.length >= LONG_TEXT_LENGTH || value.includes('\n'));
}

export function isLongTextChange(prev: unknown, next: unknown): boolean {
  return isLongTextValue(prev) || isLongTextValue(next);
}

export function getChangeStatus(prev: unknown, next: unknown): string {
  if (isUnsetValue(prev) && !isUnsetValue(next)) {
    return 'Added';
  }
  if (!isUnsetValue(prev) && isUnsetValue(next)) {
    return 'Removed';
  }

  return 'Updated';
}

export function getProseStatus(prev: unknown, summary: string): string {
  return isUnsetValue(prev) ? `${formatSimpleValue(prev)} -> ${summary}` : summary;
}

export function formatItem(value: unknown): string {
  if (isRecord(value)) {
    if (typeof value.facilityCode === 'string') {
      return formatFacilityItem(value);
    }

    const bedGroup = formatBedGroup(value);
    if (bedGroup) {
      return bedGroup;
    }

    const parts = [
      typeof value.name === 'string' ? value.name.trim() : undefined,
      value.unit ? formatSimpleValue(value.unit) : undefined,
      value.payable_at_property === true ? 'payable at property' : undefined,
      value.commissionable_type === 'non-commissionable' ? 'non-commissionable' : undefined,
    ].filter(Boolean);

    if (parts.length > 0) {
      return parts.join(' - ');
    }
  }

  return formatSimpleValue(value);
}

export function isFeaturedFacility(value: unknown): boolean {
  return isRecord(value) && typeof value.facilityCode === 'string' && value.featured === true;
}

export function isBedGroupItem(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && getBedGroupItems(value).length > 0;
}
